import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../context/ThemeContext';
import { formatPrice } from '../utils/quantities';
import { formatDisplayDate } from '../utils/dates';
import SolidMapPin from './SolidMapPin';
import TakeoffArrow from './TakeoffArrow';
import LandingArrow from './LandingArrow';

const CollapsibleLegHeader = ({
  legIndex,
  isExpanded,
  onToggleExpanded,
  onRemove,
  showRemoveButton = true,
  totalPrice = 0,
  fromAddress,
  toAddress,
  shipDate,
  deliveryDate,
  className = ''
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Format addresses for display
  const originName = fromAddress?.formatted_address || 'Origin';
  const destinationName = toAddress?.formatted_address || 'Destination';
  const routeDisplay = `${originName} to ${destinationName}`;

  // Format dates for display
  const shipDateDisplay = formatDisplayDate(shipDate);
  const deliveryDateDisplay = formatDisplayDate(deliveryDate);
    
  // Theme-based styling
  const headerClassName = clsx(
    'p-4 cursor-pointer select-none',
    {
      'md:border-b border-slate-700': isDark && isExpanded,
      'md:border-b border-gray-200': !isDark && isExpanded
    },
    className
  );

  const legTitleClassName = clsx(
    'text-lg font-semibold flex items-center gap-2',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const routeInfoClassName = clsx(
    'text-lg',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  const priceClassName = clsx(
    'text-lg font-bold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const expandIconClassName = clsx(
    'transition-colors',
    {
      'text-slate-400 hover:text-white': isDark,
      'text-gray-800 hover:text-gray-600': !isDark
    }
  );

  const removeButtonClassName = clsx(
    'transition-colors p-1 hover:bg-red-500 hover:bg-opacity-20 rounded',
    {
      'text-slate-400 hover:text-red-400': isDark,
      'text-gray-800 hover:text-red-500': !isDark
    }
  );

  const iconClassName = clsx({
    'text-slate-400': isDark,
    'text-gray-500': !isDark
  });

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <div className={headerClassName} onClick={onToggleExpanded}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown size={24} className={expandIconClassName} />
            ) : (
              <ChevronUp size={24} className={expandIconClassName} />
            )}
            <span className={legTitleClassName}>LEG {legIndex + 1}</span>
          </div>
          
          {!isExpanded && (
            <div className="flex items-center gap-4 flex-1">
              <div className="flex items-center gap-2">
                <SolidMapPin size={18} className={iconClassName} />
                <span className={routeInfoClassName}>{routeDisplay}</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  <TakeoffArrow size={18} className={iconClassName} />
                  <span className={routeInfoClassName}>{shipDateDisplay}</span>
                </div>
                <div className="flex items-center gap-1">
                  <LandingArrow size={18} className={iconClassName} />
                  <span className={routeInfoClassName}>{deliveryDateDisplay}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className={priceClassName}>{formatPrice(totalPrice)}</span>
          {showRemoveButton && (
            <button 
              onClick={handleRemoveClick}
              className={removeButtonClassName}
              aria-label="Remove leg"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

CollapsibleLegHeader.propTypes = {
  legIndex: PropTypes.number.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggleExpanded: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showRemoveButton: PropTypes.bool,
  totalPrice: PropTypes.number,
  fromAddress: PropTypes.object,
  toAddress: PropTypes.object,
  shipDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  deliveryDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date)
  ]),
  className: PropTypes.string
};

export default CollapsibleLegHeader;